import { useId, useState } from 'react';
import { ChevronDown, Clock, PenLine, Users } from 'lucide-react';
import { Badge } from '../ui/Badge';
import type { Herramienta } from '../../lib/sesion';

/**
 * Panel expandible con la ficha pedagógica de una herramienta del catálogo:
 * el XIA completo (qué se hace / por qué sirve / hacia dónde lleva), la
 * duración sugerida, los modos y lo que se captura. Para que el facilitador
 * municipal entienda QUÉ está agregando antes de agregarlo.
 *
 * Autocontenido: maneja su propio abierto/cerrado y solo necesita la
 * {@link Herramienta} (manifiesto público C3). El orquestador lo monta
 * después dentro del catálogo; no toca la selección por clic.
 */

/** Tipos de captura del manifiesto (v2 `capturas[]`, con alias v1 `captura`). */
function tiposCaptura(herramienta: Herramienta): string[] {
  const v2 = (herramienta.capturas ?? [])
    .map((c) => c.tipo)
    .filter((t): t is string => Boolean(t));
  if (v2.length > 0) return [...new Set(v2)];
  const v1 =
    typeof herramienta.captura === 'string'
      ? herramienta.captura
      : herramienta.captura?.tipo;
  return v1 ? [v1] : [];
}

/** Modos del manifiesto (v2 `modos[]`, con alias v1 `modo`). */
function modosDe(herramienta: Herramienta): string[] {
  if (herramienta.modos && herramienta.modos.length > 0) return herramienta.modos;
  return herramienta.modo ? [herramienta.modo] : [];
}

export function DetalleHerramienta({
  herramienta,
  abiertoInicial = false,
}: {
  herramienta: Herramienta;
  /** Abrir el panel de entrada (por defecto arranca plegado). */
  abiertoInicial?: boolean;
}) {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const panelId = useId();

  const capturas = tiposCaptura(herramienta);
  const modos = modosDe(herramienta);
  const xia = herramienta.xia;

  return (
    <div className="overflow-hidden rounded-md border border-border bg-surface-raised">
      <button
        type="button"
        aria-expanded={abierto}
        aria-controls={panelId}
        onClick={() => setAbierto((v) => !v)}
        className="flex min-h-touch w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus"
      >
        <span className="min-w-0 font-sans text-xs font-medium text-content-muted">
          Qué hace «{herramienta.nombre}»
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-content-muted transition-transform duration-fast ${
            abierto ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {abierto && (
        <div id={panelId} className="flex flex-col gap-3 border-t border-border px-3 py-3">
          {/* XIA completo: el método explicado en tres frases del manifiesto. */}
          <dl className="flex flex-col gap-2.5">
            <div>
              <dt className="font-display text-xs font-semibold uppercase tracking-wide text-cyan">
                Qué se hace
              </dt>
              <dd className="mt-0.5 font-sans text-sm text-content">{xia.que}</dd>
            </div>
            <div>
              <dt className="font-display text-xs font-semibold uppercase tracking-wide text-cyan">
                Por qué sirve
              </dt>
              <dd className="mt-0.5 font-sans text-sm text-content">{xia.por_que}</dd>
            </div>
            <div>
              <dt className="font-display text-xs font-semibold uppercase tracking-wide text-cyan">
                Hacia dónde lleva
              </dt>
              <dd className="mt-0.5 font-sans text-sm text-content">{xia.hacia}</dd>
            </div>
          </dl>

          {/* Ficha operativa: duración sugerida, modos y capturas. */}
          <div className="flex flex-col gap-2 border-t border-border pt-2.5">
            <p className="flex items-center gap-1.5 font-sans text-xs text-content-muted">
              <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {herramienta.duracion_min != null ? (
                <>
                  <span className="tabular-nums text-content">
                    {herramienta.duracion_min} min
                  </span>
                  de duración sugerida (se puede ajustar al agregarla)
                </>
              ) : (
                'Sin duración sugerida: la definís al agregarla.'
              )}
            </p>

            {modos.length > 0 && (
              <p className="flex flex-wrap items-center gap-1.5 font-sans text-xs text-content-muted">
                <Users className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Se trabaja:
                {modos.map((modo) => (
                  <Badge key={modo} variant="neutral">
                    {modo}
                  </Badge>
                ))}
              </p>
            )}

            {capturas.length > 0 && (
              <p className="flex flex-wrap items-center gap-1.5 font-sans text-xs text-content-muted">
                <PenLine className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                La gente aporta:
                {capturas.map((tipo) => (
                  <Badge key={tipo} variant="info">
                    {tipo}
                  </Badge>
                ))}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
